import { Row, Button } from '@keyslabor/shared/ui';
import TryAgainPopup from './TryAgainPopup';
import theme from '@keyslabor/shared/theme';
import { RecordControlRing } from './RecordControlRing';
import { RecordControlMinLabel } from './RecordControlMinLabel';

/**
 * Which round record control to render. Two designs are kept side by side so
 * they can be compared in Cosmos (`RecordButton.fixture.tsx`) before one wins:
 *  - 'ring'      radial progress ring + "Stop / N sec"        (current design)
 *  - 'min-label' no ring, "Stop" + "MIN Ns" under it          (proposed design)
 */
export type RecordControlVariant = 'ring' | 'min-label';

const RECORD_CONTROLS = {
  ring: RecordControlRing,
  'min-label': RecordControlMinLabel,
} as const;

interface RecordButtonProps {
  isRecording: boolean;
  isRecordReady: boolean;
  onNextStep: () => void;
  onRecordStart: () => void;
  onRecordStop: () => void;
  onHideRetryConfirm: () => void;
  onShowRetryConfirm: () => void;
  isShowRetryConfirm: boolean;
  timeLeft: number;
  children?: React.ReactNode;
  onRetryRecord?: () => void;
  variant?: RecordControlVariant;
}

export default function RecordButton({
  isRecordReady,
  isRecording,
  onNextStep,
  onRecordStart,
  onRecordStop,
  isShowRetryConfirm,
  onShowRetryConfirm,
  onHideRetryConfirm,
  timeLeft,
  children,
  onRetryRecord,
  variant = 'ring',
}: RecordButtonProps) {
  const RecordControl = RECORD_CONTROLS[variant];

  return (
    <>
      <Row
        mt="20px"
        width="100%"
        position="absolute"
        backgroundColor="transparent"
        flexCenter
        height="140px"
        borderTopLeftRadius="20px"
        borderTopRightRadius="20px"
        bottom="0"
        zIndex={100}
      >
        <RecordControl
          isRecording={isRecording}
          isRecordReady={isRecordReady}
          timeLeft={timeLeft}
          onRecordStart={onRecordStart}
          onRecordStop={onRecordStop}
          onShowRetryConfirm={onShowRetryConfirm}
        />
        <Button
          mt="60px"
          maxWidth="332px"
          kind="large"
          disabled={!isRecordReady}
          onClick={onNextStep}
          css={`
            &[disabled] {
              background-color: ${theme.colors.grayDisabled} !important;
            }
          `}
        >
          {children}
        </Button>
      </Row>
      <TryAgainPopup
        onHideRetryConfirm={onHideRetryConfirm}
        isShowRetryConfirm={isShowRetryConfirm}
        onRecordStart={() => {
          onRetryRecord?.();
        }}
      />
    </>
  );
}
